import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components';
import { ProgressFlowItem } from '@features/assessment';
import { SingularKeys, Trans } from '@libs/i18n';
import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react';

interface ProgressFlowCircleProps {
  item: ProgressFlowItem;
  index: number;
  isCompleted: boolean;
  isCurrent: boolean;
  isSelected?: boolean;
  isOptional?: boolean;
  isModifiedFromPrevious?: boolean;
  isNavigationDisabled?: boolean;
  onClick?: () => void;
}

export const ProgressFlowCircle: FC<ProgressFlowCircleProps> = observer(
  ({
    item,
    index,
    isCompleted,
    isCurrent,
    isSelected,
    isOptional,
    isModifiedFromPrevious,
    isNavigationDisabled,
    onClick,
  }) => {
    const isClickable = (isCompleted || isCurrent) && onClick && !isNavigationDisabled;
    const theme = useTheme();

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 0.25,
          cursor: isClickable ? 'pointer' : 'default',
          opacity: isNavigationDisabled ? 0.85 : 1,
          transition: 'opacity 0.2s',
        }}
        onClick={isClickable ? onClick : undefined}
      >
        <Box
          sx={{
            width: { xs: 26, sm: 28, md: 30 },
            height: { xs: 26, sm: 28, md: 30 },
            borderRadius: 100,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isCompleted ? 'green.700' : isCurrent ? 'blue.700' : 'grey.400',
            color: isCompleted || isCurrent ? 'common.white' : 'black.500',
            fontWeight: 600,
            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
            // Allow the edited pin to render outside the circle bounds
            overflow: 'visible',
          }}
        >
          {isCompleted ? <CheckIcon size={18} weight="bold" /> : index}

          {/* "Edited in this version" pin — rendered when the domain has
              `isModifiedFromPrevious` set on the parent comparison. Sits in
              the top-right corner so it's visible regardless of state. */}
          {isModifiedFromPrevious && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: 'purple.700',
                color: 'common.white',
                border: `2px solid ${theme.palette.common.white}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
              aria-label="Edited in this version"
            >
              <PencilSimpleIcon size={8} weight="bold" />
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

// Separate component for domain name display (used below circles)
export const ProgressFlowCircleName: FC<{
  item: ProgressFlowItem;
  isSelected?: boolean;
  isCurrent?: boolean;
  isOptional?: boolean;
}> = observer(({ item, isSelected, isCurrent, isOptional }) => {
  const shouldBeBold = isSelected === true;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
      }}
    >
      <Typography
        variant="textXS_medium"
        sx={{
          color: isSelected ? 'black.1400' : 'black.200',
          fontWeight: shouldBeBold ? 700 : 500,
          textAlign: 'center',
          maxWidth: { xs: 120, sm: 140, md: 160 },
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
        }}
      >
        {item.title}
      </Typography>
      {isOptional && (
        <Badge
          variant="Optional"
          label={<Trans i18nKey={'assessments.new.progressFlow.optional' as SingularKeys} />}
        />
      )}
    </Box>
  );
});
